import React, { useEffect, useState, useRef } from "react";
import Highcharts, { color } from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import worldMap from "@highcharts/map-collection/custom/world.geo.json";
import proj4 from "proj4";
import { renderToStaticMarkup } from "react-dom/server";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";

import { LocateFixed, Minus, Plus } from "lucide-react";
import { Button } from "../../../components/ui/button";

// Enable proj4 globally for Highcharts maps
if (typeof window !== "undefined") {
  // @ts-ignore
  window.proj4 = window.proj4 || proj4;
}
interface Collaboration {
  id: number;
  Country: string;
  Continent: string;
  City?: string;
  Lat?: number | string;
  Long?: number | string;
}
interface WorldMapData {
  TotalPartners: number;
  TotalCountries: number;
  Collaborations: Collaboration[];
}

const normalizeCountry = (name: string) => name.toLowerCase().trim();

const WorldMap: React.FC<{ data: WorldMapData }> = ({ data }) => {
  const chartRef = useRef<any>(null);
  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(
    null,
  );

  const countryNameToIso: Record<string, string> = {};
  (worldMap as any).features.forEach((f: any) => {
    const name = normalizeCountry(f.properties.name);
    const iso = f.properties["hc-key"];
    if (name && iso) countryNameToIso[name] = iso;
  });

  countryNameToIso["usa"] =
    countryNameToIso["united states"] ||
    countryNameToIso["united states of america"] ||
    "us";

  const groupedByContinent = Object.fromEntries(
    Object.entries(
      data.Collaborations.reduce(
        (acc, item) => {
          const cont = item.Continent;

          if (!acc[cont]) acc[cont] = new Set<string>();

          acc[cont].add(item.Country);

          return acc;
        },
        {} as Record<string, Set<string>>,
      ),
    ).map(([continent, countries]) => [continent, Array.from(countries)]),
  );

  useEffect(() => {
    if (!data || !worldMap) return;
    const uniqueCountries = Array.from(
      new Set(data.Collaborations.map((c) => normalizeCountry(c.Country))),
    );

    const parseCoord = (v?: number | string): number | null => {
      if (v == null) return null;
      if (typeof v === "number") return v;
      const s = String(v).trim();
      const numMatch = s.match(/-?\d+(\.\d+)?/);
      if (!numMatch) return null;
      let n = parseFloat(numMatch[0]);
      const dir = s.toUpperCase();
      if (dir.includes("S") || dir.includes("W")) n = -Math.abs(n);
      return n;
    };

    const highlightedCountries = uniqueCountries
      .map((name) => {
        const iso = countryNameToIso[name];
        if (!iso) {
          console.warn(`No ISO found for country: ${name}`);
          return null;
        }
        return { "hc-key": iso, value: 1 };
      })
      .filter(Boolean) as { "hc-key": string; value: number }[];

    const cityPoints = data.Collaborations.map((c) => {
      const lat = parseCoord(c.Lat);
      const lon = parseCoord(c.Long);
      if (lat == null || lon == null) return null;
      return {
        id: c.id,
        name: c.City || c.Country,
        country: c.Country,
        lat,
        lon,
      };
    }).filter(Boolean) as {
      id: number;
      name: string;
      country: string;
      lat: number;
      lon: number;
    }[];

    const mapPinSvg = renderToStaticMarkup(
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 48"
        width="48"
        height="72"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12c0 7 10 12 10 12s10-5 10-12c0-5.52-4.48-10-10-10z"
          fill="#DC3545"
          stroke="white"
          strokeWidth="1.5"
        />
        <circle cx="12" cy="12" r="3" fill="white" />
      </svg>,
    );
    const svgUrl = `url(data:image/svg+xml;utf8,${encodeURIComponent(mapPinSvg)})`;
    setChartOptions({
      chart: {
        map: worldMap as any,
        backgroundColor: "#f9fbfc",
      },
      accessibility: {
        enabled: true,
        description: `Interactive world map showing ${data.TotalPartners} partners across ${data.TotalCountries} countries`,
        keyboardNavigation: {
          enabled: true,
        },
        screenReaderSection: {
          beforeChartFormat: `<h3>World map of partnerships</h3><p>{chartTitle}</p><p>{chartSubtitle}</p><p>{chartLongdesc}</p>`,
        },
      },
      title: {
        text: `World map showing ${data.TotalPartners} partners in ${data.TotalCountries} countries`,
        style: { fontSize: "1px" },
        useHTML: false,
      },
      plotOptions: {
        series: {
          states: {
            inactive: { enabled: false },
            hover: { enabled: false },
          },
        },
      },
      legend: { enabled: false },
      credits: { enabled: false },
      mapNavigation: {
        enableButtons: false,
        enabled: true,
      },
      tooltip: {
        formatter: function () {
          return this.name || "";
        },
      },
      series: [
        {
          states: { hover: { color: "#DC3545" }, inactive: { enabled: false } },
          type: "map",
          name: "Countries",
          mapData: worldMap as any,
          data: highlightedCountries,
          color: "#FD9485",
          borderColor: "#d2dbe0",
          // states: { hover: { color: "#DC3545" } },
          showInLegend: false,
        },
        {
          type: "mappoint",
          name: "Cities",
          data: cityPoints.map((p) => ({
            name: p.name,
            lat: p.lat,
            lon: p.lon,
            country: p.country,
          })),
          dataLabels: { enabled: false },
          marker: {
            symbol: svgUrl,
            width: 32,
            height: 48,
          },
          tooltip: {
            pointFormat: "<b>{point.name}</b><br/>{point.country}",
          },
          enableMouseTracking: true,
        },
      ],
    });
  }, [data]);

  const handleZoomIn = () => {
    const chart = chartRef.current?.chart;
    if (chart) {
      chart.mapZoom(0.5);
    }
  };

  const handleZoomOut = () => {
    const chart = chartRef.current?.chart;
    if (chart) {
      chart.mapZoom(2);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center py-8 lg:px-8 gap-8 items-start relative">
      {/* Left section */}
      <div className="flex flex-col gap-4 w-full lg:w-auto lg:px-4 min-w-[23%]">
        <div className="flex gap-8 py-6 items-center border-b border-border-stone">
          <div>
            <div className="text-[2rem] font-bold text-body-text-teal_green">
              {data.TotalPartners}
            </div>
            <div className="text-body-text-muted text-base font-normal">
              partners
            </div>
          </div>
          <div className="text-body-text-muted text-base font-normal">in</div>
          <div>
            <div className="text-[2rem] font-bold text-body-text-teal_green">
              {data.TotalCountries}
            </div>
            <div className="text-body-text-muted text-base font-normal">
              countries
            </div>
          </div>
        </div>

        <h3 className="sr-only">Countries by Continent</h3>
        <div className="max-h-[350px] overflow-y-auto">
          {Object.entries(groupedByContinent).map(
            ([continent, countries], idx) => (
              <Accordion
                type="single"
                collapsible
                className="w-full"
                key={continent}
              >
                <AccordionItem value={`item-${idx}`}>
                  <AccordionTrigger className="font-semibold text-body-text-foreground">
                    {continent}
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-4 text-balance px-12 py-4 font-medium">
                    {Array.from(countries).map((c) => (
                      <p key={c}>{c}</p>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ),
          )}
        </div>
      </div>

      {/* Map */}
      <div className="relative w-full pt-8" style={{ minHeight: "400px" }}>
        {chartOptions && (
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="mapChart"
            options={chartOptions}
            ref={chartRef}
            containerProps={{ style: { width: "100%", height: "100%" } }}
          />
        )}

        {/* Zoom Controls */}
        <Button
          variant="outline"
          className="w-8 h-8"
          onClick={handleZoomIn}
          style={{
            position: "absolute",
            bottom: 112,
            right: 10,
            width: "20px",
          }}
          title="Zoom in"
          aria-label="Zoom in map"
        >
          <Plus />
        </Button>

        <Button
          variant="outline"
          className="w-8 h-8"
          onClick={handleZoomOut}
          style={{
            position: "absolute",
            bottom: 80,
            right: 10,
            width: "20px",
          }}
          title="Zoom out"
          aria-label="Zoom out map"
        >
          <Minus />
        </Button>

        <Button
          variant="outline"
          className="w-8 h-8"
          onClick={() => chartRef.current?.chart?.mapZoom()}
          style={{
            position: "absolute",
            bottom: 30,
            right: 10,
            width: "20px",
          }}
          title="Reset zoom"
          aria-label="Reset map zoom"
        >
          <LocateFixed />
        </Button>
      </div>
    </div>
  );
};

export default WorldMap;
